import jStat from 'jstat';

export interface KurtosisResult {
  // Sample (G2) kurtosis measures
  sampleKurtosis: number;
  sampleExcessKurtosis: number;
  
  // Population kurtosis measures
  populationKurtosis: number;
  populationExcessKurtosis: number;
  
  // Pearson's measure (b2)
  pearsonKurtosis: number;
  pearsonExcessKurtosis: number;
  
  type: 'Leptokurtic' | 'Mesokurtic' | 'Platykurtic';
  interpretation: string;
  data: number[];
}

export function calculateKurtosis(data: number[]): KurtosisResult {
  if (data.length < 4) {
    const defaultValues = {
      sampleKurtosis: 3,
      sampleExcessKurtosis: 0,
      populationKurtosis: 3,
      populationExcessKurtosis: 0,
      pearsonKurtosis: 3,
      pearsonExcessKurtosis: 0,
      type: 'Mesokurtic' as const,
      interpretation: 'Not enough data points to calculate meaningful kurtosis (minimum 4 required)',
      data
    };
    return defaultValues;
  }

  const n = data.length;
  const mean = jStat.mean(data);
  const sampleStdDev = jStat.stdev(data, false);

  if (sampleStdDev === 0) {
    const defaultValues = {
      sampleKurtosis: 3,
      sampleExcessKurtosis: 0,
      populationKurtosis: 3,
      populationExcessKurtosis: 0,
      pearsonKurtosis: 3,
      pearsonExcessKurtosis: 0,
      type: 'Mesokurtic' as const,
      interpretation: 'All values are identical, kurtosis is undefined but set to normal distribution value',
      data
    };
    return defaultValues;
  }

  // Calculate standardized fourth moment
  const standardizedData = data.map(x => (x - mean) / sampleStdDev);
  const fourthMoment = standardizedData.reduce((sum, z) => sum + Math.pow(z, 4), 0) / n;
  
  // Calculate sample excess kurtosis with bias correction
  const sampleExcessKurtosis = ((n + 1) * fourthMoment - 3 * (n - 1)) * (n - 1) / ((n - 2) * (n - 3));
  const sampleKurtosis = sampleExcessKurtosis + 3;

  // Calculate population measures (without bias correction)
  const populationExcessKurtosis = fourthMoment - 3;
  const populationKurtosis = populationExcessKurtosis + 3;

  // Pearson's measure uses a slightly different correction
  const pearsonExcessKurtosis = (n * fourthMoment) / Math.pow(sampleStdDev, 4) - 3;
  const pearsonKurtosis = pearsonExcessKurtosis + 3;
  
  // Determine distribution type and interpretation based on excess kurtosis
  let type: 'Leptokurtic' | 'Mesokurtic' | 'Platykurtic';
  let interpretation: string;
  
  if (sampleExcessKurtosis > 0) {
    type = 'Leptokurtic';
    interpretation = 'Distribution has heavier tails with more extreme values than a normal distribution';
  } else if (sampleExcessKurtosis < 0) {
    type = 'Platykurtic';
    interpretation = 'Distribution has lighter tails with fewer extreme values than a normal distribution';
  } else {
    type = 'Mesokurtic';
    interpretation = 'Distribution has similar tail weight and frequency of extreme values as a normal distribution';
  }

  return {
    sampleKurtosis,
    sampleExcessKurtosis,
    populationKurtosis,
    populationExcessKurtosis,
    pearsonKurtosis,
    pearsonExcessKurtosis,
    type,
    interpretation,
    data
  };
} 